import {
  boolean,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
  uuid,
  index,
  uniqueIndex,
  check,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

// ---------------------------------------------------------------------------
// Timestamp helper
// ---------------------------------------------------------------------------

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Status enums
// ---------------------------------------------------------------------------

export const DOMAIN_STATUSES = [
  "pending",
  "verifying",
  "active",
  "failed",
] as const;

export const SSL_STATUSES = [
  "pending",
  "pending_validation",
  "pending_issuance",
  "active",
  "expired",
  "failed",
] as const;

export type DomainStatus = (typeof DOMAIN_STATUSES)[number];
export type SslStatus = (typeof SSL_STATUSES)[number];

// ---------------------------------------------------------------------------
// DOM-018 verification reason vocabulary (decision packet anchor)
// Stable codes emitted on `domain.verification_state_changed@v1` for the
// UI-visible failure surface. Treat as an append-only enum — extensions are
// fine, removals require a registry update.
// ---------------------------------------------------------------------------

export const DOMAIN_VERIFICATION_REASONS = [
  "NS_NOT_FOUND",
  "NS_MISMATCH",
  "TXT_NOT_FOUND",
  "TXT_MISMATCH",
  "CNAME_MISMATCH",
  "TIMEOUT",
  "RATE_LIMITED",
  "PROVIDER_ERROR",
] as const;

export type DomainVerificationReason =
  (typeof DOMAIN_VERIFICATION_REASONS)[number];

// ---------------------------------------------------------------------------
// Custom Domains
// ---------------------------------------------------------------------------

export const customDomains = pgTable(
  "custom_domains",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    domain: text("domain").notNull(),
    status: text("status").notNull().default("pending"),
    cloudflareZoneId: text("cloudflare_zone_id"),
    sslStatus: text("ssl_status"),
    // DOM-010 Phase 2 (per DOM-010-DECISION-PACKET.md "Minimal contract"
    // §Schema): three nullable columns the future unified
    // `domain-verifier` worker writes on every CF Custom Hostnames SSL
    // poll. The companion `ssl_status` column is broadened in the same
    // migration to accept the Phase 1 vocabulary
    // `CF_CUSTOM_HOSTNAME_SSL_STATUSES` (which adds `pending_validation`
    // and `pending_issuance` to the legacy four-state set). Mirror the
    // Phase 1 catalogs in `./cf-custom-hostname-ssl-vocabulary.ts`
    // (`CF_CUSTOM_HOSTNAME_SSL_STATUSES`,
    // `CF_CUSTOM_HOSTNAME_SSL_FAILURE_REASONS`) — the CHECK constraints
    // below inline the literals so the DB fails closed on drift.
    sslStatusUpdatedAt: timestamp("ssl_status_updated_at", tz),
    sslStatusReason: text("ssl_status_reason"),
    cfCustomHostnameId: text("cf_custom_hostname_id"),
    isPlatformPurchased: boolean("is_platform_purchased").notNull().default(false),
    registrar: text("registrar"),
    registrarDomainId: text("registrar_domain_id"),
    nameservers: text("nameservers").array(),
    // SES email identity provisioning (DN-T.3)
    sesIdentityArn: text("ses_identity_arn"),
    sesDkimTokens: text("ses_dkim_tokens").array(),
    sesVerificationStatus: text("ses_verification_status"),
    sesVerifiedAt: timestamp("ses_verified_at", tz),
    // DOM-008 Phase 2 (per DOM-008-DECISION-PACKET.md "Minimal contract"):
    // SES verification surface columns the future unified `domain-verifier`
    // worker writes on every poll. Mirror the Phase 1 closed catalogs in
    // `./ses-verification-vocabulary.ts` (`SES_VERIFICATION_STATUSES`,
    // `SES_VERIFICATION_FAILURE_REASONS`) — the CHECK constraints below
    // inline the literals so the DB fails closed on drift.
    //
    // sesVerificationFailureReason: closed reason code from
    // SES_VERIFICATION_FAILURE_REASONS when status is `failed`. NULL on
    // pending/verifying/success. Auditors replay verbatim — the order
    // pinned by Phase 1 is the wire-level contract.
    sesVerificationFailureReason: text("ses_verification_failure_reason"),
    // sesVerificationStatusChangedAt: timestamp of the most recent
    // verifier-driven status transition. Updated on every legal transition
    // per `isLegalSesVerificationTransition` so the UI badge can render
    // "Last checked Xm ago" alongside the verdict, and so the poller can
    // throttle re-checks within an SLA budget.
    sesVerificationStatusChangedAt: timestamp(
      "ses_verification_status_changed_at",
      tz,
    ),
    // sesVerificationAttempts: monotonically increasing per-row attempt
    // counter. Mirrors `verificationAttempts` on the CNAME-side DOM-018
    // surface so the unified verifier writes one counter per side. NULL
    // legal for legacy rows; new writers default to 0.
    sesVerificationAttempts: integer("ses_verification_attempts"),
    // DNS scan cache (DN-T.1: DoH-based scanner results)
    dnsScanResult: jsonb("dns_scan_result").$type<{
      mx?: string[];
      spf?: string | null;
      dkim?: boolean;
      dmarc?: string | null;
      emailProvider?: string | null;
      scannedAt?: string;
    }>(),
    dnsScanUpdatedAt: timestamp("dns_scan_updated_at", tz),
    // Services enabled on this domain (pages, email, booking, etc.)
    servicesEnabled: text("services_enabled").array().default([]),
    expiresAt: timestamp("expires_at", tz),
    verifiedAt: timestamp("verified_at", tz),
    // DOM-018: verification state machine (pending|verifying|active|failed)
    // separate from legacy free-text `status` (kept populated for back-compat).
    verificationState: text("verification_state").notNull().default("pending"),
    verificationReason: text("verification_reason"), // DomainVerificationReason | null
    verificationAttempts: integer("verification_attempts").notNull().default(0),
    lastVerificationAt: timestamp("last_verification_at", tz),
    verificationDeadlineAt: timestamp("verification_deadline_at", tz),
    // DOM-022 (per DOM-022-CLASSIFICATION-DECISION-LOCK-PACKET.md, Axis A2):
    // explicit per-row personal-data flag. Default false — most custom_domain
    // rows are tenant-owned routing artifacts, not personal data. The flag is
    // set true only when the row carries WHOIS contact PII (e.g. platform-
    // purchased domains where the platform holds the registrant identity) so
    // DSAR collectors can filter on `is_personal_data = true` and the erasure
    // handler can invoke the CF Registrar privacy-proxy / anonymization path.
    //
    // `dsar_classification_reason` is the human-auditable rationale captured
    // when the flag is set (e.g. "platform_purchased_whois", "agency_owned").
    // Kept nullable so existing rows are not retroactively classified.
    isPersonalData: boolean("is_personal_data").notNull().default(false),
    dsarClassificationReason: text("dsar_classification_reason"),
    // DOM-011 Phase 2 (per DOM-011-DECISION-PACKET.md "Schema (one migration,
    // additive)"): external / coexistence SSL surface. These columns
    // distinguish Cloudflare-managed SSL (DOM-010 path: CF Custom Hostnames)
    // from coexistence SSL (tenant keeps existing DNS host + existing
    // certificate issuer) and capture the periodic HTTPS-handshake probe
    // result the future DOM-018 worker writes. Mirror the Phase 1 closed
    // catalogs in `@ease/dns-scanner/dom-011-external-ssl-guidance.ts`
    // (`EXTERNAL_SSL_PROVIDERS`, `EXTERNAL_SSL_STATUSES`,
    // `EXTERNAL_SSL_REASONS`) — CHECK constraints below inline the literals
    // so the DB fails closed on drift.
    //
    // setupStrategy: top-level discriminator the wizard branches on.
    //   - cloudflare    (DOM-010 — platform manages DNS + SSL via CF)
    //   - coexistence   (DOM-011 — tenant keeps existing DNS host + SSL)
    //   - nameserver    (DOM-005 — NS transferred to the platform)
    // Nullable for legacy rows that pre-date the wizard; null is treated
    // as "unknown" by the UI badge.
    setupStrategy: text("setup_strategy"),
    // setupProvider: tenant's hosting provider, used by the coexistence
    // wizard to pick the right SSL guidance fork. Mirrors EXTERNAL_SSL_PROVIDERS
    // exactly. Nullable: unset until the wizard captures the answer.
    setupProvider: text("setup_provider"),
    // externalSslStatus: result of the most recent coexistence HTTPS probe.
    // Mirrors EXTERNAL_SSL_STATUSES exactly. Nullable: unset until the first
    // probe lands.
    externalSslStatus: text("external_ssl_status"),
    // externalSslCheckedAt: when the most recent probe ran. The future
    // DOM-018 worker uses this to throttle re-checks within an SLA budget.
    externalSslCheckedAt: timestamp("external_ssl_checked_at", tz),
    // externalSslExpiresAt: cert `notAfter` from the most recent probe.
    // The probe classifier in Phase 1 surfaces `expiring_soon` (within
    // EXTERNAL_SSL_EXPIRY_WARNING_DAYS=30) and `expired` based on this value.
    externalSslExpiresAt: timestamp("external_ssl_expires_at", tz),
    // externalSslIssuer: human-readable issuer name from the cert chain
    // (e.g. "Let's Encrypt", "Google Trust Services"). Surfaced verbatim in
    // the UI so the operator can tell whether the cert was auto-issued or
    // manually installed. Nullable: unset until the first successful probe.
    externalSslIssuer: text("external_ssl_issuer"),
    // externalSslReason: closed reason code from EXTERNAL_SSL_REASONS when
    // status is non-trivial (e.g. `cert_chain_untrusted`, `cert_expired`).
    // Nullable: NULL on `issued` status, populated on `pending`/`unknown`/
    // `expired`/`invalid`/`unreachable`/`expiring_soon`.
    externalSslReason: text("external_ssl_reason"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("idx_custom_domains_domain").on(table.domain),
    index("idx_custom_domains_tenant").on(table.tenantId),
    index("idx_custom_domains_tenant_status").on(
      table.tenantId,
      table.status,
    ),
    check(
      "chk_custom_domains_status",
      sql`${table.status} IN ('pending', 'verifying', 'active', 'failed')`,
    ),
    // DOM-010 Phase 2: broadened SSL-status vocabulary mirroring
    // CF_CUSTOM_HOSTNAME_SSL_STATUSES in
    // `./cf-custom-hostname-ssl-vocabulary.ts`. Adds `pending_validation`
    // and `pending_issuance` to the legacy four-state set so the future
    // verifier can distinguish DCV-in-progress from issuance-in-progress.
    // Legacy `pending` is kept for back-compat with existing rows; new
    // writers MUST use the Phase 1 vocabulary going forward.
    check(
      "chk_custom_domains_ssl_status",
      sql`${table.sslStatus} IS NULL OR ${table.sslStatus} IN ('pending', 'pending_validation', 'pending_issuance', 'active', 'expired', 'failed')`,
    ),
    // DOM-010 Phase 2: closed SSL failure-reason vocabulary mirroring
    // CF_CUSTOM_HOSTNAME_SSL_FAILURE_REASONS verbatim. Auditors replay
    // the reason string in the `domain.ssl_state_changed@v1` event
    // payload — the order pinned by Phase 1 is the wire-level contract.
    check(
      "chk_custom_domains_ssl_status_reason",
      sql`${table.sslStatusReason} IS NULL OR (${table.sslStatus} IS NOT NULL AND ${table.sslStatus} = 'failed' AND ${table.sslStatusReason} IN ('validation_timed_out', 'dcv_error', 'internal_error'))`,
    ),
    check(
      "chk_custom_domains_verification_state",
      sql`${table.verificationState} IN ('pending', 'verifying', 'active', 'failed')`,
    ),
    check(
      "chk_custom_domains_verification_reason",
      sql`${table.verificationReason} IS NULL OR ${table.verificationReason} IN ('NS_NOT_FOUND', 'NS_MISMATCH', 'TXT_NOT_FOUND', 'TXT_MISMATCH', 'CNAME_MISMATCH', 'TIMEOUT', 'RATE_LIMITED', 'PROVIDER_ERROR')`,
    ),
    index("idx_custom_domains_verification_state").on(
      table.verificationState,
    ),
    // DOM-022: partial index supports DSAR collector — most rows have
    // `is_personal_data = false` so a partial index keeps storage proportional
    // to the personal-data subset that the collector actually scans.
    index("idx_custom_domains_personal_data")
      .on(table.tenantId)
      .where(sql`${table.isPersonalData} = true`),
    // DOM-011 Phase 2: closed setup-strategy vocabulary. Three strategies
    // map to the DOM-002 wizard branches (Cloudflare-managed / coexistence /
    // nameserver transfer). NULL legal for legacy rows.
    check(
      "chk_custom_domains_setup_strategy",
      sql`${table.setupStrategy} IS NULL OR ${table.setupStrategy} IN ('cloudflare', 'coexistence', 'nameserver')`,
    ),
    // DOM-011 Phase 2: closed provider vocabulary mirroring
    // EXTERNAL_SSL_PROVIDERS in `@ease/dns-scanner`. The list MUST stay in
    // lockstep — adding a provider in Phase 1 requires a fresh migration
    // here.
    check(
      "chk_custom_domains_setup_provider",
      sql`${table.setupProvider} IS NULL OR ${table.setupProvider} IN ('godaddy', 'namecheap', 'cloudflare_dns_only', 'google_domains', 'squarespace_domains', 'shopify', 'wix', 'wordpress_com', 'generic')`,
    ),
    // DOM-011 Phase 2: closed external-SSL status vocabulary mirroring
    // EXTERNAL_SSL_STATUSES in `@ease/dns-scanner`. The DOM-018 polling
    // worker writes one of these values; any future addition requires a
    // fresh migration to widen this CHECK.
    check(
      "chk_custom_domains_external_ssl_status",
      sql`${table.externalSslStatus} IS NULL OR ${table.externalSslStatus} IN ('unknown', 'pending', 'issued', 'expiring_soon', 'expired', 'invalid', 'unreachable')`,
    ),
    // DOM-011 Phase 2: closed reason vocabulary mirroring
    // EXTERNAL_SSL_REASONS in `@ease/dns-scanner`. Auditors replay reason
    // strings verbatim — the order here pins the wire-level contract.
    check(
      "chk_custom_domains_external_ssl_reason",
      sql`${table.externalSslReason} IS NULL OR ${table.externalSslReason} IN ('no_probe_yet', 'tls_handshake_ok', 'tls_handshake_failed', 'tcp_connect_failed', 'cert_chain_untrusted', 'cert_hostname_mismatch', 'cert_self_signed', 'cert_expired', 'cert_expiring_within_14d', 'cert_expiring_within_30d', 'provider_reported_invalid')`,
    ),
    // DOM-011 Phase 2: tenant-scoped index over coexistence rows for the
    // DOM-018 polling worker's per-tick scan. Partial so storage stays
    // proportional to the coexistence subset (CF-managed rows are the
    // majority).
    index("idx_custom_domains_external_ssl")
      .on(table.tenantId, table.externalSslCheckedAt)
      .where(sql`${table.setupStrategy} = 'coexistence'`),
    // DOM-008 Phase 2: closed SES verification-status vocabulary mirroring
    // SES_VERIFICATION_STATUSES in `./ses-verification-vocabulary.ts`.
    // The future unified `domain-verifier` worker writes one of these
    // values to `sesVerificationStatus`; any addition in Phase 1 requires
    // a fresh migration to widen this CHECK.
    check(
      "chk_custom_domains_ses_verification_status",
      sql`${table.sesVerificationStatus} IS NULL OR ${table.sesVerificationStatus} IN ('pending', 'verifying', 'success', 'failed')`,
    ),
    // DOM-008 Phase 2: closed SES failure-reason vocabulary mirroring
    // SES_VERIFICATION_FAILURE_REASONS in `./ses-verification-vocabulary.ts`.
    // Auditors replay reason strings verbatim — the order pinned by Phase 1
    // is the wire-level contract for the future
    // `email.ses_verification_state_changed@v1` CQRS event payload.
    check(
      "chk_custom_domains_ses_verification_failure_reason",
      sql`${table.sesVerificationFailureReason} IS NULL OR ${table.sesVerificationFailureReason} IN ('DKIM_NOT_FOUND', 'DKIM_PARTIAL', 'MX_MISMATCH', 'TIMEOUT', 'IAM_PERMISSION')`,
    ),
    // DOM-008 Phase 2: non-negative attempt counter. NULL legal for
    // legacy rows; new writers default to 0.
    check(
      "chk_custom_domains_ses_verification_attempts_nonneg",
      sql`${table.sesVerificationAttempts} IS NULL OR ${table.sesVerificationAttempts} >= 0`,
    ),
  ],
);
